// Main recording hook - orchestrates session, camera, upload, and finalize

import { useCallback, useEffect, useRef } from 'react'
import { useCamera } from './useCamera'
import { useMediaRecorder, getExtensionFromMimeType } from './useMediaRecorder'
import { useRecordingStore, type RecordingStatus } from '@/lib/store'
import { apiClient } from '@/lib/api'

interface UseRecordingReturn {
  // Camera
  videoRef: React.RefObject<HTMLVideoElement>
  isCameraReady: boolean
  cameraError: string | null

  // Recording state
  status: RecordingStatus
  isRecording: boolean
  chunksUploaded: number
  recordingDuration: number
  error: string | null

  // Location
  includeLocation: boolean
  toggleLocation: () => void

  // Last video
  lastVideoId: string | null
  videoFormat: string // 'mp4' or 'webm'

  // Actions
  initialize: () => Promise<void>
  startRecording: () => Promise<void>
  stopRecording: () => Promise<string | null>
  reset: () => void
}

export function useRecording(): UseRecordingReturn {
  const store = useRecordingStore()
  const durationIntervalRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)

  // Camera setup
  const camera = useCamera({ facingMode: 'environment' })

  // Handle chunk upload
  const handleChunk = useCallback(async (chunk: Blob, chunkNumber: number) => {
    try {
      await apiClient.uploadChunk(chunk, chunkNumber)
      store.incrementChunks()
    } catch (error) {
      console.error('Chunk upload failed:', error)
      // In future: queue in IndexedDB for retry
      // For now, log error but continue recording
    }
  }, [store])

  // MediaRecorder setup
  const recorder = useMediaRecorder({
    stream: camera.stream,
    onChunk: handleChunk,
    chunkInterval: 5000, // 5 seconds
  })

  // Duration tracking
  const startDurationTracking = useCallback(() => {
    startTimeRef.current = Date.now()
    durationIntervalRef.current = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
      store.setRecordingDuration(elapsed)
    }, 1000)
  }, [store])

  const stopDurationTracking = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
  }, [])

  // Initialize - get camera ready
  const initialize = useCallback(async () => {
    store.setStatus('initializing')
    store.setError(null)

    try {
      await camera.startCamera()
      store.setStatus('ready')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to initialize camera'
      store.setStatus('error')
      store.setError(message)
    }
  }, [camera.startCamera, store])

  // Start recording flow
  const startRecording = useCallback(async () => {
    if (!camera.isReady) {
      store.setError('Camera not ready')
      return
    }

    try {
      store.setStatus('recording')
      store.setError(null)

      // Create a FRESH recording session (not reusing feed session)
      const token = await apiClient.createRecordingSession()
      store.setSessionToken(token)

      // Get location if enabled
      if (store.includeLocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            })
          })
          store.setLocation(position.coords.latitude, position.coords.longitude)
        } catch {
          // Location failed, continue without it
          console.log('Location unavailable, continuing without GPS')
        }
      }

      // Start recording
      recorder.startRecording()
      startDurationTracking()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start recording'
      store.setError(message)
    }
  }, [camera.isReady, recorder, store, startDurationTracking])

  // Stop recording and finalize
  const stopRecording = useCallback(async (): Promise<string | null> => {
    try {
      // Stop recording
      recorder.stopRecording()
      stopDurationTracking()
      store.setStatus('uploading')

      // Wait for all chunks to finish uploading (with 30s timeout)
      const allUploaded = await apiClient.waitForUploads(30000)
      if (!allUploaded) {
        const status = apiClient.getUploadStatus()
        console.warn('Upload status at finalize:', status)
      }

      // Additional delay to ensure last chunk is processed
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Verify we have at least one successful upload
      const status = apiClient.getUploadStatus()
      if (status.successful === 0) {
        throw new Error('No chunks were uploaded successfully. Please try again.')
      }

      // Finalize video with correct file extension
      const fileExtension = getExtensionFromMimeType(recorder.mimeType)
      const result = await apiClient.finalizeVideo({
        gpsLat: store.gpsLat ?? undefined,
        gpsLng: store.gpsLng ?? undefined,
        fileExtension,
      })

      store.setStatus('complete')
      store.setLastVideoId(result.videoId)

      return result.videoId
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to finalize video'
      store.setError(message)
      store.setStatus('error')
      return null
    }
  }, [recorder, store, stopDurationTracking])

  // Reset for new recording
  const reset = useCallback(() => {
    stopDurationTracking()
    apiClient.clearRecordingSession()
    store.reset()
  }, [store, stopDurationTracking])

  // Emergency auto-save when page is closing or going to background
  // This saves the video even if the user can't press stop (phone taken, etc.)
  useEffect(() => {
    const handleEmergencySave = () => {
      if (recorder.isRecording) {
        console.log('Emergency save triggered - attempting to finalize')
        // Stop the recorder to flush final chunk
        try {
          recorder.stopRecording()
        } catch {
          // May fail if already stopped
        }
        // Fire-and-forget finalize using sendBeacon
        apiClient.emergencyFinalize({
          gpsLat: store.gpsLat ?? undefined,
          gpsLng: store.gpsLng ?? undefined,
          fileExtension: getExtensionFromMimeType(recorder.mimeType),
        })
      }
    }

    // Visibility change - page went to background (phone locked, app switched, etc.)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && recorder.isRecording) {
        handleEmergencySave()
      }
    }

    // Page is about to close/navigate away
    const handleBeforeUnload = () => {
      if (recorder.isRecording) {
        handleEmergencySave()
      }
    }

    // iOS Safari prefers pagehide
    const handlePageHide = () => {
      if (recorder.isRecording) {
        handleEmergencySave()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [recorder.isRecording, store.gpsLat, store.gpsLng, recorder])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDurationTracking()
    }
  }, [stopDurationTracking])

  return {
    // Camera
    videoRef: camera.videoRef,
    isCameraReady: camera.isReady,
    cameraError: camera.error,

    // Recording state
    status: store.status,
    isRecording: recorder.isRecording,
    chunksUploaded: store.chunksUploaded,
    recordingDuration: store.recordingDuration,
    error: store.error,

    // Location
    includeLocation: store.includeLocation,
    toggleLocation: store.toggleLocation,

    // Last video
    lastVideoId: store.lastVideoId,
    videoFormat: getExtensionFromMimeType(recorder.mimeType),

    // Actions
    initialize,
    startRecording,
    stopRecording,
    reset,
  }
}
